/**
 * This file is a generic fetch client that does not know about React or store lifecycles.
 * It should be treated as simply a convencience wrapper around the generated API client.
 */

import { SimplifyDeep } from 'type-fest'

import env from '~/env'
import { useStore } from '~/store/store'

import { createBaseClient } from './base-client'
import { components, paths } from './dips-types.generated'
import { dipsSessionRevocationMiddleware } from './unauthorizedHandlingMiddleware'

export const getBaseHeaders = () => ({
    Authorization: `Bearer ${useStore.getState().user.dipsApiAccessToken}`,
    // Makes IAP answer an expired session with 401 instead of a 302 to its sign-in page, which fetch
    // would follow cross-origin and surface as an opaque network error.
    ...(env.VITE_DIPS_BEHIND_IAP && { 'X-Requested-With': 'XMLHttpRequest' }),
})

export function isDipsUrlConfigured() {
    return Boolean(env.VITE_DIPS_BASE_URL.trim())
}

export function getClient() {
    const client = createBaseClient<paths>({
        baseUrl: env.VITE_DIPS_BASE_URL,
        headers: getBaseHeaders(),
    })
    client.use(dipsSessionRevocationMiddleware)
    return client
}

type RemoveIndex<T> = {
    [K in keyof T as string extends K ? never : number extends K ? never : symbol extends K ? never : K]: T[K]
}

type WithoutIndexSignature<T> = SimplifyDeep<{
    [K in keyof T]: RemoveIndex<T[K]>
}>

// Removing the index signature to make it better work with the denormalizer, which can't handle index signatures yet.
export type DipsSchemas = WithoutIndexSignature<components['schemas']>
